import { useState, useEffect, useMemo, type FC } from "react";
import { useSearchParams } from "react-router-dom";
import {
  revokeCertificate,
  useCertificates,
} from "../stores/certificate_store";
import { useDeviceGroups } from "../stores/device_group_store";
import { useCertificatePolicies } from "../stores/certificate_policy_store";
import type {
  Certificate,
  CertificateStatus,
} from "../../../shared/models/certificate";
import { daysUntil } from "../lib/date_utils";
import { useCustomerId } from "../stores/customer_id_store";
import { UnassignedPlaceholder } from "../components/unassigned_placeholder";
import { Pagination } from "../components/pagination";
import { TimeRangeButtons } from "../components/time_range_buttons";
import { modalManager } from "../lib/modal_store";

type Tab = "all" | "active" | "expiring" | "expired" | "revoked";

const STATUS_BADGE: Record<CertificateStatus, { cls: string; label: string }> =
  {
    active: { cls: "vmx-badge-success", label: "Active" },
    expiring: { cls: "vmx-badge-warning", label: "Expiring Soon" },
    expired: { cls: "vmx-badge-danger", label: "Expired" },
    revoked: { cls: "vmx-badge-neutral", label: "Revoked" },
  };

export const Certificates: FC = () => {
  const customerId = useCustomerId();
  const certificates = useCertificates();
  const groups = useDeviceGroups();
  const policies = useCertificatePolicies();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groupFilter, setGroupFilter] = useState("");
  const [policyFilter, setPolicyFilter] = useState("");
  const [selectedDeviceIds, setSelectedClientIds] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sync groupId and policyId query params into filters on mount
  useEffect(() => {
    const groupId = searchParams.get("groupId");
    if (groupId) setGroupFilter(groupId);
    const policyId = searchParams.get("policyId");
    if (policyId) setPolicyFilter(policyId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tabParam = searchParams.get("tab");
  const tab: Tab =
    tabParam === "active" ||
    tabParam === "expiring" ||
    tabParam === "expired" ||
    tabParam === "revoked"
      ? tabParam
      : "all";
  const deviceIdSearch = searchParams.get("deviceId") ?? "";
  const issuedFrom = searchParams.get("issuedFrom") ?? "";
  const issuedTo = searchParams.get("issuedTo") ?? "";

  const updateUrlFilters = (
    updates: Partial<
      Record<
        "tab" | "deviceId" | "issuedFrom" | "issuedTo" | "groupId" | "policyId",
        string | undefined
      >
    >
  ) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (key === "tab" && value === "all")
      ) {
        next.delete(key);
        return;
      }
      next.set(key, value);
    });

    setSearchParams(next, { replace: true });
  };

  const groupMap = useMemo(() => {
    const m = new Map<string, string>();
    groups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const policyMap = useMemo(() => {
    const m = new Map<string, string>();
    policies.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [policies]);

  const policyById = useMemo(() => {
    const m = new Map<string, (typeof policies)[number]>();
    policies.forEach((p) => m.set(p.id, p));
    return m;
  }, [policies]);

  const groupById = useMemo(() => {
    const m = new Map<string, (typeof groups)[number]>();
    groups.forEach((g) => m.set(g.id, g));
    return m;
  }, [groups]);

  // Unique group IDs that appear in certificates
  const certGroupIds = useMemo(() => {
    const s = new Set<string>();
    certificates.forEach((c) => s.add(c.deviceGroupId));
    return Array.from(s);
  }, [certificates]);

  // Unique policy IDs that appear in certificates
  const certPolicyIds = useMemo(() => {
    const s = new Set<string>();
    certificates.forEach((c) => s.add(c.certificatePolicyId));
    return Array.from(s);
  }, [certificates]);

  const expiringCerts = useMemo(
    () =>
      certificates
        .filter((c) => c.status === "expiring")
        .sort(
          (a, b) =>
            new Date(a.validTo).getTime() - new Date(b.validTo).getTime()
        ),
    [certificates]
  );

  const revokedCerts = useMemo(
    () =>
      certificates
        .filter((c) => c.status === "revoked")
        .sort(
          (a, b) =>
            new Date(b.revokedAt ?? b.validFrom).getTime() -
            new Date(a.revokedAt ?? a.validFrom).getTime()
        ),
    [certificates]
  );

  // Apply filters
  const filtered = useMemo(() => {
    let list: Certificate[];

    switch (tab) {
      case "active":
        list = certificates.filter((c) => c.status === "active");
        break;
      case "expiring":
        list = expiringCerts;
        break;
      case "expired":
        list = certificates.filter((c) => c.status === "expired");
        break;
      case "revoked":
        list = revokedCerts;
        break;
      default:
        list = certificates;
    }

    if (groupFilter !== "") {
      list = list.filter((c) => c.deviceGroupId === groupFilter);
    }
    if (policyFilter !== "") {
      list = list.filter((c) => c.certificatePolicyId === policyFilter);
    }
    if (deviceIdSearch.trim() !== "") {
      const q = deviceIdSearch.trim().toLowerCase();
      list = list.filter((c) => c.deviceId.toLowerCase().includes(q));
    }
    if (issuedFrom !== "") {
      const fromMs = new Date(`${issuedFrom}T00:00:00`).getTime();
      list = list.filter((c) => new Date(c.validFrom).getTime() >= fromMs);
    }
    if (issuedTo !== "") {
      const toMs = new Date(`${issuedTo}T23:59:59.999`).getTime();
      list = list.filter((c) => new Date(c.validFrom).getTime() <= toMs);
    }

    return list.sort(
      (a, b) =>
        new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
    );
  }, [
    tab,
    certificates,
    expiringCerts,
    revokedCerts,
    groupFilter,
    policyFilter,
    deviceIdSearch,
    issuedFrom,
    issuedTo,
  ]);

  // Clear selection and reset page when any filter changes
  useEffect(() => {
    setSelectedClientIds(new Set());
    setCurrentPage(1);
  }, [tab, groupFilter, policyFilter, deviceIdSearch, issuedFrom, issuedTo]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCerts = filtered.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  // Only active/expiring certificates on the current page are revocable
  const revocablePaginated = useMemo(
    () =>
      paginatedCerts.filter(
        (c) => c.status === "active" || c.status === "expiring"
      ),
    [paginatedCerts]
  );

  const openGroupDetail = (groupId: string) => {
    const group = groupById.get(groupId);
    if (!group) return;
    modalManager.open("groupDetail", {
      group,
      policy: policyById.get(group.certificatePolicyId),
      onPolicyClick: () => {
        const p = policyById.get(group.certificatePolicyId);
        if (p) modalManager.open("policyDetail", { policy: p });
      },
    });
  };

  const openCertDetail = (cert: Certificate) => {
    modalManager.open("certificateDetail", {
      cert,
      groupName: groupMap.get(cert.deviceGroupId),
      policyName: policyMap.get(cert.certificatePolicyId),
      onGroupClick: () => openGroupDetail(cert.deviceGroupId),
      onPolicyClick: () => {
        const p = policyById.get(cert.certificatePolicyId);
        if (p) modalManager.open("policyDetail", { policy: p });
      },
      onRevoke: () => {
        modalManager.open("revokeCertificate", {
          certificate: cert,
          onConfirm: () => revokeCertificate(cert.deviceId, cert.validFrom),
        });
      },
    });
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "all", label: "All Certificates" },
    { key: "active", label: "Active" },
    { key: "expiring", label: "Expiring Soon" },
    { key: "expired", label: "Expired" },
    { key: "revoked", label: "Revoked" },
  ];

  if (!customerId) {
    return <UnassignedPlaceholder />;
  }

  return (
    <div className="vmx-page" style={{ position: "relative" }}>
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">🔒 Certificates</div>
          <div className="vmx-page-subtitle">
            Monitor and manage certificates issued by SecureCycle. Track status,
            expiration, and revocation.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="vmx-cert-tabs"
        style={{
          display: "flex",
          gap: 4,
          marginBottom: 16,
          borderBottom: "2px solid var(--vmx-gray-100)",
          paddingBottom: 0,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => updateUrlFilters({ tab: t.key })}
            style={{
              padding: "10px 18px",
              fontSize: "0.8125rem",
              fontWeight: tab === t.key ? 700 : 500,
              color:
                tab === t.key ? "var(--vmx-accent)" : "var(--vmx-gray-500)",
              background: "none",
              border: "none",
              borderBottom:
                tab === t.key
                  ? "2px solid var(--vmx-accent)"
                  : "2px solid transparent",
              marginBottom: -2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "all var(--vmx-transition)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div
        className="vmx-cert-filters"
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          className="vmx-input"
          placeholder="Search by device ID…"
          value={deviceIdSearch}
          onChange={(e) => updateUrlFilters({ deviceId: e.target.value })}
          style={{ maxWidth: 280 }}
        />
        <div className="vmx-date-row">
          <label
            htmlFor="vmx-cert-date-from"
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--vmx-gray-700)",
            }}
          >
            From
          </label>
          <input
            id="vmx-cert-date-from"
            className="vmx-input"
            type="date"
            value={issuedFrom}
            placeholder="From"
            onChange={(e) => updateUrlFilters({ issuedFrom: e.target.value })}
            style={{ maxWidth: 180 }}
          />
          <label
            htmlFor="vmx-cert-date-to"
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--vmx-gray-700)",
            }}
          >
            To
          </label>
          <input
            id="vmx-cert-date-to"
            className="vmx-input"
            type="date"
            value={issuedTo}
            placeholder="To"
            onChange={(e) => updateUrlFilters({ issuedTo: e.target.value })}
            style={{ maxWidth: 180 }}
          />
        </div>
        <TimeRangeButtons
          startDate={issuedFrom}
          endDate={issuedTo}
          onSelect={(_key, start, end) =>
            updateUrlFilters({ issuedFrom: start, issuedTo: end })
          }
        />
        <select
          className="vmx-select"
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          <option value="">All device groups</option>
          {certGroupIds.map((gid) => (
            <option key={gid} value={gid}>
              {groupMap.get(gid) ?? gid}
            </option>
          ))}
        </select>
        <select
          className="vmx-select"
          value={policyFilter}
          onChange={(e) => setPolicyFilter(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          <option value="">All policies</option>
          {certPolicyIds.map((pid) => (
            <option key={pid} value={pid}>
              {policyMap.get(pid) ?? pid}
            </option>
          ))}
        </select>
        {(groupFilter !== "" ||
          policyFilter !== "" ||
          deviceIdSearch !== "" ||
          issuedFrom !== "" ||
          issuedTo !== "") && (
          <button
            className="vmx-btn vmx-btn-ghost vmx-btn-sm"
            onClick={() => {
              setGroupFilter("");
              setPolicyFilter("");
              updateUrlFilters({
                deviceId: undefined,
                issuedFrom: undefined,
                issuedTo: undefined,
                groupId: undefined,
                policyId: undefined,
              });
            }}
          >
            Clear filters
          </button>
        )}
        {revocablePaginated.length > 0 && (
          <button
            className="vmx-btn vmx-btn-ghost vmx-btn-sm vmx-mobile-select-all"
            onClick={() => {
              const allSelected =
                revocablePaginated.length > 0 &&
                revocablePaginated.every((c) =>
                  selectedDeviceIds.has(c.deviceId)
                );
              if (allSelected) {
                setSelectedClientIds(new Set());
              } else {
                setSelectedClientIds(
                  new Set(revocablePaginated.map((c) => c.deviceId))
                );
              }
            }}
          >
            {revocablePaginated.length > 0 &&
            revocablePaginated.every((c) => selectedDeviceIds.has(c.deviceId))
              ? "Deselect All"
              : "Select All"}
          </button>
        )}
        {selectedDeviceIds.size > 0 && (
          <button
            className="vmx-btn vmx-btn-danger vmx-btn-sm"
            onClick={() =>
              modalManager.open("bulkRevoke", {
                certificates: Array.from(selectedDeviceIds).map((id) => {
                  const cert = certificates.find((c) => c.deviceId === id);
                  return { deviceId: id, certvalidfrom: cert?.validFrom ?? "" };
                }),
                onComplete: () => {
                  setSelectedClientIds(new Set());
                  modalManager.closeAll();
                },
              })
            }
          >
            Revoke ({selectedDeviceIds.size})
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">🔒</div>
            <div className="vmx-empty-title">No certificates found</div>
            <div className="vmx-empty-text">
              {!customerId
                ? "You need to be assigned to a customer to view certificates."
                : certificates.length === 0
                  ? "Certificates will appear here once devices connect and are issued certificates by SecureCycle."
                  : "No certificates match the current filters."}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="vmx-table-wrapper vmx-card-table-wrapper vmx-certs-table-wrapper">
            <table className="vmx-table vmx-card-table vmx-certs-table">
              <thead>
                <tr>
                  <th>
                    {revocablePaginated.length > 0 && (
                      <input
                        type="checkbox"
                        checked={
                          revocablePaginated.length > 0 &&
                          revocablePaginated.every((c) =>
                            selectedDeviceIds.has(c.deviceId)
                          )
                        }
                        ref={(el) => {
                          if (el) {
                            const allSelected =
                              revocablePaginated.length > 0 &&
                              revocablePaginated.every((c) =>
                                selectedDeviceIds.has(c.deviceId)
                              );
                            const someSelected =
                              !allSelected &&
                              revocablePaginated.some((c) =>
                                selectedDeviceIds.has(c.deviceId)
                              );
                            el.indeterminate = someSelected;
                          }
                        }}
                        onChange={() => {
                          const allSelected =
                            revocablePaginated.length > 0 &&
                            revocablePaginated.every((c) =>
                              selectedDeviceIds.has(c.deviceId)
                            );
                          if (allSelected) {
                            setSelectedClientIds(new Set());
                          } else {
                            setSelectedClientIds(
                              new Set(revocablePaginated.map((c) => c.deviceId))
                            );
                          }
                        }}
                      />
                    )}
                  </th>
                  <th>Device ID</th>
                  <th>Device Group</th>
                  <th>Policy</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedCerts.map((c) => {
                  const badge = STATUS_BADGE[c.status];
                  const days = daysUntil(c.validTo);
                  return (
                    <tr
                      key={c.deviceId}
                      onClick={() => openCertDetail(c)}
                      style={{ cursor: "pointer" }}
                    >
                      <td data-label="">
                        {(c.status === "active" || c.status === "expiring") && (
                          <input
                            type="checkbox"
                            checked={selectedDeviceIds.has(c.deviceId)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                              setSelectedClientIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(c.deviceId)) {
                                  next.delete(c.deviceId);
                                } else {
                                  next.add(c.deviceId);
                                }
                                return next;
                              });
                            }}
                          />
                        )}
                      </td>
                      <td
                        data-label="Device ID"
                        style={{
                          fontWeight: 600,
                          color: "var(--vmx-gray-800)",
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "var(--vmx-font-mono)",
                            fontSize: "0.8125rem",
                          }}
                        >
                          {c.deviceId}
                        </span>
                      </td>
                      <td data-label="Group">
                        {groupMap.has(c.deviceGroupId) ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openGroupDetail(c.deviceGroupId);
                            }}
                            style={{
                              color: "var(--vmx-accent-dark)",
                              fontWeight: 600,
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                            }}
                          >
                            {groupMap.get(c.deviceGroupId)}
                          </button>
                        ) : (
                          <span
                            style={{
                              fontFamily: "var(--vmx-font-mono)",
                              fontSize: "0.8125rem",
                              color: "var(--vmx-gray-500)",
                            }}
                          >
                            {c.deviceGroupId}
                          </span>
                        )}
                      </td>
                      <td data-label="Policy">
                        {policyMap.has(c.certificatePolicyId) ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const p = policyById.get(c.certificatePolicyId);
                              if (p)
                                modalManager.open("policyDetail", {
                                  policy: p,
                                });
                            }}
                            style={{
                              color: "var(--vmx-accent-dark)",
                              fontWeight: 600,
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                            }}
                          >
                            {policyMap.get(c.certificatePolicyId)}
                          </button>
                        ) : (
                          <span
                            style={{
                              fontFamily: "var(--vmx-font-mono)",
                              fontSize: "0.8125rem",
                              color: "var(--vmx-gray-500)",
                            }}
                          >
                            {c.certificatePolicyId}
                          </span>
                        )}
                      </td>
                      <td data-label="Status">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span className={`vmx-badge ${badge.cls}`}>
                            {badge.label}
                          </span>
                          {c.status === "expiring" && (
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                color: "var(--vmx-warning)",
                                fontWeight: 600,
                              }}
                            >
                              {days}d left
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="">
                        {(c.status === "active" || c.status === "expiring") && (
                          <button
                            className="vmx-btn vmx-btn-danger vmx-btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              modalManager.open("revokeCertificate", {
                                certificate: c,
                                onConfirm: () =>
                                  revokeCertificate(c.deviceId, c.validFrom),
                              });
                            }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </>
      )}
    </div>
  );
};
